import { useCallback, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import {
  CustomSelect,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { TextArea } from '@/components/inputs/TextArea'
import { NumericInput } from '@/components/inputs/NumericInput'
import { DateInput } from '@/components/inputs/DateInput'
import type {
  BoolQuestion,
  Question,
  SelectQuestion,
  SelectQuestionOption,
} from '@/features/jobs/questions/questions'
import { CreateOCRScopeJobsDialogManualQuestion } from '@/features/jobs/scoping/components/CreateOCRScopeJobsDialog'
import { type CreateOCRScopeJobsViewModel } from './CreateOCRScopeJobsViewModel'

export interface OCRManualQuestionsInputContainerProps {
  vm: CreateOCRScopeJobsViewModel
  question: Question
  testId?: string
}

export const OCRManualQuestionsInputContainer = observer(
  function OCRManualQuestionsInputContainer({
    vm,
    question,
    testId,
  }: OCRManualQuestionsInputContainerProps) {
    switch (question.type) {
      case 'Bool':
        return (
          <CreateOCRScopeJobsDialogManualQuestion title={question.title}>
            <OCRManualQuestionsBoolInputContainer
              vm={vm}
              question={question}
              testId={testId}
            />
          </CreateOCRScopeJobsDialogManualQuestion>
        )
      case 'Select':
        return (
          <CreateOCRScopeJobsDialogManualQuestion title={question.title}>
            <OCRManualQuestionsSelectInputContainer
              vm={vm}
              selectQuestion={question}
              testId={testId}
            />
          </CreateOCRScopeJobsDialogManualQuestion>
        )
      case 'Text':
        return (
          <CreateOCRScopeJobsDialogManualQuestion title={question.title}>
            <TextArea
              autoSize
              rows={1}
              placeholder="Enter value here"
              value={vm.answerFor(question.id) as string}
              onChange={(e) => vm.updateAnswer(e.target.value, question.id)}
            />
          </CreateOCRScopeJobsDialogManualQuestion>
        )
      case 'Numeric':
        return (
          <CreateOCRScopeJobsDialogManualQuestion title={question.title}>
            <NumericInput
              value={vm.answerFor(question.id) as number}
              precision={
                question.format.type === 'Decimal' ? question.format.places : 0
              }
              placeholder="Enter value here"
              onChange={(value) => vm.updateAnswer(value, question.id)}
            />
          </CreateOCRScopeJobsDialogManualQuestion>
        )
      case 'DateTime': {
        const answer = vm.answerFor(question.id)
        return (
          <CreateOCRScopeJobsDialogManualQuestion title={question.title}>
            <DateInput
              value={
                answer instanceof Date ? answer : new Date(answer as string)
              }
              onChange={(value) => vm.updateAnswer(value, question.id)}
              includeTime={question.collectTime}
              icon="dropdown"
            />
          </CreateOCRScopeJobsDialogManualQuestion>
        )
      }
      case 'Documents':
      case 'Breaker':
      case 'Unknown':
        return undefined
    }
  },
)

export interface OCRManualQuestionsBoolInputContainerProps {
  vm: CreateOCRScopeJobsViewModel
  question: BoolQuestion
  testId?: string
}

export const OCRManualQuestionsBoolInputContainer = observer(
  function OCRManualQuestionsBoolInputContainer({
    vm,
    question,
    testId,
  }: OCRManualQuestionsBoolInputContainerProps) {
    const itemSource = useMemo(() => {
      return ItemSource.static<string>(['Yes', 'No'], (item, text) =>
        item.toLowerCase().includes(text.toLowerCase()),
      )
    }, [])
    const itemToString = useCallback((itemName: string) => itemName, [])
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })

    return (
      <CustomSelect
        {...selectProps}
        multi={false}
        icon="dropdown"
        iconColor="var(--grey600)"
        value={vm.answerFor(question.id) === true ? 'Yes' : 'No'}
        onChange={(value) => vm.updateAnswer(value === 'Yes', question.id)}
        renderItem={(itemProps) => (
          <SingleItemSelectedDefault
            {...itemProps}
            item={itemProps}
            itemToString={(i) => String(i.item)}
          />
        )}
        renderSearchItem={({
          item,
          itemToString: _itemToString,
          ...itemProps
        }) => <SearchResultItem {...itemProps}>{item}</SearchResultItem>}
        itemToString={itemToString}
        testId={testId ? `${testId}-question-${question.id}-select` : undefined}
      />
    )
  },
)

export interface OCRManualQuestionsSelectInputContainerProps {
  vm: CreateOCRScopeJobsViewModel
  selectQuestion: SelectQuestion
  testId?: string
}

export const OCRManualQuestionsSelectInputContainer = observer(
  function OCRManualQuestionsSelectInputContainer({
    vm,
    selectQuestion,
    testId,
  }: OCRManualQuestionsSelectInputContainerProps) {
    const itemSource = useMemo(
      () =>
        ItemSource.static<SelectQuestionOption>(
          selectQuestion.options,
          (item, text) => item.text.toLowerCase().includes(text.toLowerCase()),
        ),
      [selectQuestion.options],
    )
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })
    const itemToString = useCallback(
      (item: SelectQuestionOption) => item.text,
      [],
    )

    return (
      <CustomSelect
        {...selectProps}
        multi={selectQuestion.multi}
        icon="dropdown"
        iconColor="var(--grey600)"
        value={
          vm.answerFor(selectQuestion.id) === ''
            ? undefined
            : selectQuestion.multi
              ? (vm.answerFor(selectQuestion.id) as
                  SelectQuestionOption[] | undefined)
              : (vm.answerFor(selectQuestion.id) as SelectQuestionOption)
        }
        onChange={(value) =>
          vm.updatePotentialMultiSelectAnswer(
            value,
            selectQuestion.id,
            selectQuestion.multi,
            selectQuestion.multi ? value : undefined,
          )
        }
        placeholder={
          selectQuestion.multi ? 'Select all that apply' : 'Select one'
        }
        itemToString={itemToString}
        persist={selectQuestion.multi}
        testId={
          testId ? `${testId}-question-${selectQuestion.id}-select` : undefined
        }
      />
    )
  },
)
